import {useCallback} from 'react';

import {useReadOnlyDataHook} from 'src/hooks/use-read-only-data-hook';
import {getUserActiveTemplates} from 'src/openapi/admin/apis/UserSearchTemplateApi';
import {SearchType as SearchTypes} from 'src/openapi/admin/models/SearchType';
import {SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {TemplateType as TemplateTypes} from 'src/openapi/admin/models/TemplateType';
import {useAppDispatch} from 'src/store/hooks';

import type {SearchType} from 'src/openapi/admin/models/SearchType';
import type {SortDirection} from 'src/openapi/admin/models/SortDirection';
import type {ErrorPayload} from 'src/typedef';

type Props = {
    adminId: number,
    searchType: SearchType,
    onLoad: (columns: string[], sortField: string, sortDirection: SortDirection) => void,
};

export const useGetActiveSearchTemplate = ({adminId, searchType, onLoad}: Props) => {
    const apiDispatch = useAppDispatch();

    const getDispatchAction = useCallback(async () => {
        const response = await apiDispatch(getUserActiveTemplates({
            userId: adminId,
            searchType,
        }));

        if (response.payload.success) {
            const responseData = response.payload.data;
            if (responseData) {
                const selectedColumns = Object.entries(responseData.columns)
                    .map(([code, order]) => ({code, order}))
                    .sort((a, b) => a.order - b.order)
                    .map(({code}) => code);

                onLoad(
                    selectedColumns,
                    response.payload.data.defaultSortColumn || 'EMAIL',
                    response.payload.data.defaultSortDirection || SortDirections.ASC,
                );
            } else {
                Promise.resolve({type: '', payload: {
                    error: true,
                    success: false,
                    data: {message: 'No data received from the server'},
                } as ErrorPayload});
            }
        }

        return Promise.resolve({type: '', payload: response.payload});
    }, [adminId, onLoad, apiDispatch, searchType]);

    return useReadOnlyDataHook({
        initValue: {
            templateId: -1,
            userId: -1,
            templateName: '',
            defaultSortColumn: '',
            defaultSortDirection: SortDirections.ASC,
            templateType: TemplateTypes.DEFAULT,
            searchType: SearchTypes.USER,
            isActive: false,
            columns: {},
        },
        getDispatchAction,
    });
};
